import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowLeft, Shuffle } from "lucide-react";
import { api } from "../lib/api";

// A curated pool of distinct sample letters covering every language,
// every duration tier, and a different emotional register each.
// The /preview page rotates through these so the operator can review
// how Claude's reply varies across context, language, and time scale.
const SAMPLES = [
  {
    key: "zh-hk-365-confession",
    user_name: "之遠",
    language_code: "zh-HK",
    duration_days: 365,
    register: "深夜懺悔 · 廣東話語感",
    letter_content:
      "今晚的心情，比想像中還要沉重。我總跟每個人說我很好。寫下這封信，只是因為我需要一個能誠實安置所有情緒的地方。如果你正在讀這封信，請對我溫柔一點，至少，要比今晚的我更懂得溫柔。",
  },
  {
    key: "en-90-burnout",
    user_name: "Marcus",
    language_code: "en",
    duration_days: 90,
    register: "Burnout · literary English",
    letter_content:
      "I'm writing this from the office at 11:47 pm, and the only honest thing I can say is that I no longer recognize the person I've become this year. I keep telling everyone I'm 'fine, just busy.' The truth is I'm scared I optimized myself into a stranger. Please remember, three months from now, that I noticed. That I cared enough to write this down. That this was the night I stopped pretending.",
  },
  {
    key: "zh-cn-180-heartbreak",
    user_name: "舒云",
    language_code: "zh-CN",
    duration_days: 180,
    register: "失恋后六个月 · 简体诗意",
    letter_content:
      "今天是分手后的第十一天。我以为我已经准备好，但下班回家在地铁上还是哭了。没有人看见，也没有人问。我只是想告诉半年后的自己——如果那时你已经笑得出来了，请不要忘记今晚这个在车厢里悄悄掉眼泪的我。她也很勇敢，她只是太累了。",
  },
  {
    key: "zh-tw-365-existential",
    user_name: "宥彤",
    language_code: "zh-TW",
    duration_days: 365,
    register: "存在迷惘 · 繁體文藝",
    letter_content:
      "二十九歲的最後一個月。我擁有別人眼中算得上「完整」的一切，卻在每個沒人看見的時刻，覺得自己像個冒牌貨。我不知道自己真正想要什麼，只知道現在這條路走得越來越喘。一年後的我，如果你已經找到答案，請替今晚的我，輕輕說一句：辛苦了。",
  },
  {
    key: "en-180-new-parent",
    user_name: "Hana",
    language_code: "en",
    duration_days: 180,
    register: "New parent · tender English",
    letter_content:
      "She's six weeks old tonight, asleep on my chest, and I am crying because I'm not sure I'm doing any of this right. Everyone tells me to enjoy every moment. Nobody tells you how lonely 3 a.m. can feel even when you're holding everything you ever wanted. Future me, in six months — if she's laughing, if I'm sleeping, please remember the woman who wrote this. She was doing her best.",
  },
];

export default function PreviewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [index, setIndex] = useState(() => Math.floor(Math.random() * SAMPLES.length));
  const iframeRef = useRef(null);

  const sample = SAMPLES[index];

  const fetchPreview = useCallback((s) => {
    setLoading(true);
    setError("");
    setData(null);
    return api
      .post("/preview/render", {
        user_name: s.user_name,
        letter_content: s.letter_content,
        language_code: s.language_code,
        duration_days: s.duration_days,
      })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchPreview(sample);
  }, [index, sample, fetchPreview]);

  useEffect(() => {
    if (data && iframeRef.current) {
      iframeRef.current.srcdoc = data.html;
    }
  }, [data]);

  const handleShuffle = () => {
    // Move to next sample (rotate) so each click guarantees a different one.
    setIndex((i) => (i + 1) % SAMPLES.length);
  };

  return (
    <div data-testid="preview-page" className="min-h-screen px-6 py-12 md:py-16">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--wax)] mb-10">
          <ArrowLeft size={12} /> Back to landing
        </Link>

        <p className="text-[11px] uppercase tracking-[0.42em] text-[var(--wax)] mb-4">
          — Live production mockup · sample {index + 1} / {SAMPLES.length} —
        </p>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight text-[var(--ink)] mb-3">
          The masterpiece preview.
        </h1>
        <p className="text-[15px] text-[var(--ink-soft)] italic mb-8">
          A simulated midnight unlock. Real Claude reply. Exact production HTML.
        </p>

        {/* Shuffle control */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <button
            data-testid="preview-shuffle-btn"
            onClick={handleShuffle}
            disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-[var(--ink)] text-[12px] uppercase tracking-[0.24em] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--bg-paper)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Shuffle size={13} /> {loading ? "Calling Claude…" : "Regenerate with another sample"}
          </button>
          <span className="text-[12px] text-[var(--ink-mute)] italic">
            Rotates language, duration & emotional register.
          </span>
        </div>

        {/* The exact input being sent to Claude */}
        <div className="mb-8 border border-[var(--rule)] bg-[var(--bg-paper-soft,#F3EFE6)] p-5 md:p-6" data-testid="preview-input-card">
          <p className="text-[10px] uppercase tracking-[0.34em] text-[var(--ink-mute)] mb-3">
            Input · {sample.register}
          </p>
          <div className="grid grid-cols-3 gap-4 mb-4 text-[12px] text-[var(--ink-soft)]">
            <div>
              <span className="block uppercase tracking-[0.2em] text-[var(--ink-mute)] text-[10px] mb-1">Name</span>
              <span className="font-cn text-[var(--ink)]" data-testid="preview-input-name">{sample.user_name}</span>
            </div>
            <div>
              <span className="block uppercase tracking-[0.2em] text-[var(--ink-mute)] text-[10px] mb-1">Language</span>
              <span className="text-[var(--ink)]" data-testid="preview-input-lang">{sample.language_code}</span>
            </div>
            <div>
              <span className="block uppercase tracking-[0.2em] text-[var(--ink-mute)] text-[10px] mb-1">Duration</span>
              <span className="text-[var(--ink)]" data-testid="preview-input-duration">{sample.duration_days} days</span>
            </div>
          </div>
          <p
            className={`${sample.language_code === "en" ? "font-display" : "font-cn"} text-[15px] md:text-[16px] leading-[1.75] text-[var(--ink)] italic`}
            data-testid="preview-input-letter"
          >
            「{sample.letter_content}」
          </p>
        </div>

        {/* Subject + meta */}
        {data && (
          <div className="mb-6 pb-6 border-b border-dashed border-[var(--rule)] text-[13px] text-[var(--ink-soft)] space-y-1.5">
            <div><span className="uppercase tracking-[0.22em] text-[var(--ink-mute)] mr-3">Subject</span> <span className="font-cn text-[var(--ink)]" data-testid="preview-subject">{data.subject}</span></div>
            <div><span className="uppercase tracking-[0.22em] text-[var(--ink-mute)] mr-3">From</span> MiProse &lt;postman@miprose.com&gt;</div>
            <div><span className="uppercase tracking-[0.22em] text-[var(--ink-mute)] mr-3">Prescription</span> {data.prescription_tag} · {data.duration_days} d</div>
          </div>
        )}

        {/* Email iframe */}
        {loading && (
          <div className="border border-[var(--rule)] p-16 flex items-center justify-center text-[var(--ink-soft)] italic gap-3" data-testid="preview-loading">
            <Loader2 className="animate-spin" /> Calling Claude · rendering the email…
          </div>
        )}
        {error && (
          <div className="border border-[var(--rule)] p-10 text-[var(--wax-deep)] italic" data-testid="preview-error">{error}</div>
        )}
        {data && !loading && (
          <iframe
            key={index}
            ref={iframeRef}
            title="email preview"
            data-testid="preview-iframe"
            className="w-full bg-[var(--bg-paper)] border border-[var(--rule)] shadow-[0_24px_60px_-22px_rgba(28,26,23,0.4)]"
            style={{ minHeight: 900, border: 0 }}
          />
        )}
      </div>
    </div>
  );
}
